/**
 * Mobile Bookings — list, edit, delete confirmed bookings on /app?p=bookings
 */
import React, { useState, useEffect } from 'react';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

const T = {
  black: '#FFFFFF', dark: '#FAFAFA', panel: '#FFFFFF', card: '#FFFFFF',
  border: '#E5E5E5', red: '#CC0000', gold: '#A8862F', green: '#15803D',
  amber: '#B45309', white: '#0A0A0A', light: '#525252', dim: '#A3A3A3',
};

const fmt$ = (n) => `$${Math.round(Number(n || 0)).toLocaleString()}`;

const inputStyle = (extra = {}) => ({
  background: T.dark, border: `1px solid ${T.border}`, color: T.white,
  padding: '10px 12px', fontSize: 13, outline: 'none', boxSizing: 'border-box',
  fontFamily: 'inherit', width: '100%', ...extra,
});

const Field = ({ label, children }) => (
  <div>
    <label style={{ fontSize: 9, color: T.dim, textTransform: 'uppercase', letterSpacing: '0.2em', fontWeight: 700, display: 'block', marginBottom: 4 }}>{label}</label>
    {children}
  </div>
);

export function MobileBookings({ onOpenClient }) {
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showDeleted, setShowDeleted] = useState(false);
  const [editing, setEditing] = useState(null);
  const [reloadTick, setReloadTick] = useState(0);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        // Always fetch including deleted — partition client-side so both tab
        // counts (Active + Archive) are accurate without an extra request.
        const r = await fetch(`${API}/api/command-center/confirmed-bookings?include_deleted=true`);
        const d = await r.json();
        if (cancelled) return;
        const list = (d.bookings || []).sort((a, b) =>
          String(b.event_date || '').localeCompare(String(a.event_date || ''))
        );
        setBookings(list);
      } catch (e) { toast.error('Failed to load bookings'); }
      if (!cancelled) setLoading(false);
    })();
    return () => { cancelled = true; };
  }, [reloadTick]);

  if (editing) {
    return (
      <BookingEditor
        booking={editing}
        onCancel={() => setEditing(null)}
        onSaved={() => { setEditing(null); setReloadTick(t => t + 1); }}
        onDeleted={() => { setEditing(null); setReloadTick(t => t + 1); }}
        onRestored={() => { setEditing(null); setReloadTick(t => t + 1); }}
        onOpenClient={onOpenClient}
      />
    );
  }

  const upcoming = bookings.filter(b => b.cc_status !== 'deleted' && new Date(b.event_date) >= new Date(new Date().toISOString().slice(0, 10)));
  const past = bookings.filter(b => b.cc_status !== 'deleted' && new Date(b.event_date) < new Date(new Date().toISOString().slice(0, 10)));
  const deleted = bookings.filter(b => b.cc_status === 'deleted');

  return (
    <div data-testid="mobile-bookings">
      <p style={{ fontSize: 9, color: T.red, textTransform: 'uppercase', letterSpacing: '0.3em', fontWeight: 700, margin: '0 4px 8px' }}>Bookings</p>

      <div style={{ display: 'flex', gap: 6, marginBottom: 12 }}>
        <button
          onClick={() => setShowDeleted(false)}
          style={{
            flex: 1, padding: '8px 10px',
            background: !showDeleted ? T.red : T.card, color: !showDeleted ? T.white : T.light,
            border: `1px solid ${!showDeleted ? T.red : T.border}`,
            fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.1em', cursor: 'pointer',
          }}
          data-testid="bookings-tab-active"
        >Active ({upcoming.length + past.length})</button>
        <button
          onClick={() => setShowDeleted(true)}
          style={{
            flex: 1, padding: '8px 10px',
            background: showDeleted ? T.red : T.card, color: showDeleted ? T.white : T.light,
            border: `1px solid ${showDeleted ? T.red : T.border}`,
            fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.1em', cursor: 'pointer',
          }}
          data-testid="bookings-tab-archive"
        >Archive ({deleted.length})</button>
      </div>

      {loading && <p style={{ color: T.dim, fontSize: 12, textAlign: 'center', padding: 20 }} data-testid="bookings-loading">Loading…</p>}

      {!loading && !showDeleted && upcoming.length > 0 && (
        <>
          <p style={{ fontSize: 9, color: T.green, textTransform: 'uppercase', letterSpacing: '0.2em', fontWeight: 700, margin: '4px 4px 6px' }}>Upcoming · {upcoming.length}</p>
          <BookingList items={upcoming} onTap={setEditing} testidPrefix="upcoming" />
        </>
      )}

      {!loading && !showDeleted && past.length > 0 && (
        <>
          <p style={{ fontSize: 9, color: T.dim, textTransform: 'uppercase', letterSpacing: '0.2em', fontWeight: 700, margin: '14px 4px 6px' }}>Past · {past.length}</p>
          <BookingList items={past} onTap={setEditing} testidPrefix="past" />
        </>
      )}

      {!loading && showDeleted && (
        <>
          {deleted.length === 0
            ? <p style={{ padding: 20, textAlign: 'center', color: T.dim, fontSize: 11, border: `1px dashed ${T.border}` }}>Archive is empty.</p>
            : <BookingList items={deleted} onTap={setEditing} testidPrefix="archive" />
          }
        </>
      )}

      {!loading && !showDeleted && upcoming.length === 0 && past.length === 0 && (
        <div style={{ padding: 30, textAlign: 'center', color: T.dim, fontSize: 12, border: `1px dashed ${T.border}` }} data-testid="bookings-empty">
          No bookings yet. Create one from the Quote Builder.
        </div>
      )}
    </div>
  );
}

const BookingList = ({ items, onTap, testidPrefix }) => (
  <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
    {items.map(b => (
      <button
        key={b.booking_id}
        onClick={() => onTap(b)}
        style={{ background: T.card, border: `1px solid ${T.border}`, padding: 12, textAlign: 'left', cursor: 'pointer', color: T.white }}
        data-testid={`${testidPrefix}-row-${b.booking_id}`}
      >
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 8 }}>
          <div style={{ minWidth: 0, flex: 1 }}>
            <p style={{ fontSize: 13, fontWeight: 800, margin: 0, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{b.client_name || 'Walk-in'}</p>
            <p style={{ fontSize: 11, color: T.light, margin: '2px 0 0', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{b.vehicle || b.vehicle_name || '—'}</p>
            <p style={{ fontSize: 10, color: T.dim, margin: '2px 0 0' }}>
              {(b.event_date || '').slice(0, 10)}{b.pickup_time ? ` · ${b.pickup_time}` : ''}{b.hours ? ` · ${b.hours}h` : ''}
            </p>
          </div>
          <div style={{ textAlign: 'right' }}>
            <p style={{ fontSize: 12, color: T.gold, fontFamily: 'DM Mono, monospace', fontWeight: 700, margin: 0 }}>{fmt$(b.total)}</p>
            <p style={{ fontSize: 9, color: b.cc_status === 'deleted' ? T.red : T.light, margin: '2px 0 0', textTransform: 'uppercase', letterSpacing: '0.1em' }}>{b.cc_status || 'upcoming'}</p>
          </div>
        </div>
      </button>
    ))}
  </div>
);

const BookingEditor = ({ booking, onCancel, onSaved, onDeleted, onRestored, onOpenClient }) => {
  const [draft, setDraft] = useState({
    client_name: booking.client_name || '',
    client_phone: booking.client_phone || '',
    client_email: booking.client_email || '',
    vehicle: booking.vehicle || booking.vehicle_name || '',
    event_date: (booking.event_date || '').slice(0, 10),
    pickup_time: booking.pickup_time || '',
    pickup_location: booking.pickup_location || '',
    dropoff_location: booking.dropoff_location || '',
    hours: booking.hours || 0,
    rate: booking.rate || 0,
    total: booking.total || 0,
    notes: booking.notes || '',
    cc_status: booking.cc_status || 'upcoming',
  });
  const [busy, setBusy] = useState(false);
  const isDeleted = booking.cc_status === 'deleted';

  const save = async () => {
    setBusy(true);
    try {
      const r = await fetch(`${API}/api/command-center/confirmed-bookings/${booking.booking_id}`, {
        method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(draft),
      });
      if (!r.ok) throw new Error('Save failed');
      toast.success('Booking updated');
      onSaved();
    } catch (e) { toast.error(e.message); }
    setBusy(false);
  };

  const remove = async () => {
    if (!window.confirm(`Delete booking ${booking.booking_id}? It can be restored from the archive.`)) return;
    setBusy(true);
    try {
      const r = await fetch(`${API}/api/command-center/confirmed-bookings/${booking.booking_id}`, { method: 'DELETE' });
      if (!r.ok) throw new Error('Delete failed');
      toast.success('Booking moved to archive');
      onDeleted();
    } catch (e) { toast.error(e.message); }
    setBusy(false);
  };

  const restore = async () => {
    setBusy(true);
    try {
      const r = await fetch(`${API}/api/command-center/confirmed-bookings/${booking.booking_id}/restore`, { method: 'POST' });
      if (!r.ok) throw new Error('Restore failed');
      toast.success('Booking restored');
      onRestored();
    } catch (e) { toast.error(e.message); }
    setBusy(false);
  };

  return (
    <div data-testid="booking-editor">
      <button onClick={onCancel} style={{ background: 'none', border: 0, color: T.red, fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.1em', cursor: 'pointer', padding: '0 0 12px' }} data-testid="booking-editor-back">← Bookings</button>

      <p style={{ fontSize: 9, color: isDeleted ? T.red : T.gold, textTransform: 'uppercase', letterSpacing: '0.3em', fontWeight: 800, margin: '0 0 4px' }}>
        {isDeleted ? 'Archived Booking' : 'Edit Booking'} · {booking.booking_id}
      </p>
      <h2 style={{ fontSize: 18, fontWeight: 900, color: T.white, margin: '0 0 16px' }}>{draft.client_name || 'Walk-in'}</h2>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
        <Field label="Client Name"><input value={draft.client_name} onChange={e => setDraft({ ...draft, client_name: e.target.value })} style={inputStyle()} data-testid="edit-client-name" /></Field>
        <Field label="Phone"><input value={draft.client_phone} onChange={e => setDraft({ ...draft, client_phone: e.target.value })} type="tel" inputMode="tel" style={inputStyle()} data-testid="edit-client-phone" /></Field>
        <Field label="Email"><input value={draft.client_email} onChange={e => setDraft({ ...draft, client_email: e.target.value })} type="email" inputMode="email" style={inputStyle()} data-testid="edit-client-email" /></Field>
        <Field label="Vehicle"><input value={draft.vehicle} onChange={e => setDraft({ ...draft, vehicle: e.target.value })} style={inputStyle()} data-testid="edit-vehicle" /></Field>
        <div style={{ display: 'flex', gap: 6 }}>
          <div style={{ flex: 1 }}><Field label="Event Date"><input type="date" value={draft.event_date} onChange={e => setDraft({ ...draft, event_date: e.target.value })} style={inputStyle()} data-testid="edit-event-date" /></Field></div>
          <div style={{ flex: 1 }}><Field label="Pickup Time"><input type="time" value={draft.pickup_time} onChange={e => setDraft({ ...draft, pickup_time: e.target.value })} style={inputStyle()} data-testid="edit-pickup-time" /></Field></div>
        </div>
        <Field label="Pickup Location"><input value={draft.pickup_location} onChange={e => setDraft({ ...draft, pickup_location: e.target.value })} style={inputStyle()} data-testid="edit-pickup-location" /></Field>
        <Field label="Dropoff Location"><input value={draft.dropoff_location} onChange={e => setDraft({ ...draft, dropoff_location: e.target.value })} style={inputStyle()} data-testid="edit-dropoff-location" /></Field>
        <div style={{ display: 'flex', gap: 6 }}>
          <div style={{ flex: 1 }}><Field label="Hours"><input type="number" min="0" step="0.5" value={draft.hours} onChange={e => setDraft({ ...draft, hours: Number(e.target.value) })} style={inputStyle()} data-testid="edit-hours" /></Field></div>
          <div style={{ flex: 1 }}><Field label="Rate $/hr"><input type="number" min="0" value={draft.rate} onChange={e => setDraft({ ...draft, rate: Number(e.target.value) })} style={inputStyle()} data-testid="edit-rate" /></Field></div>
          <div style={{ flex: 1 }}><Field label="Total"><input type="number" min="0" value={draft.total} onChange={e => setDraft({ ...draft, total: Number(e.target.value) })} style={inputStyle()} data-testid="edit-total" /></Field></div>
        </div>
        <Field label="Status">
          <select value={draft.cc_status} onChange={e => setDraft({ ...draft, cc_status: e.target.value })} style={inputStyle({ color: T.white })} data-testid="edit-status">
            <option value="upcoming" style={{ background: T.dark }}>Upcoming</option>
            <option value="completed" style={{ background: T.dark }}>Completed</option>
            <option value="cancelled" style={{ background: T.dark }}>Cancelled</option>
            <option value="no_show" style={{ background: T.dark }}>No-Show</option>
          </select>
        </Field>
        <Field label="Notes">
          <textarea value={draft.notes} onChange={e => setDraft({ ...draft, notes: e.target.value })} rows={3} style={inputStyle({ resize: 'vertical' })} data-testid="edit-notes" />
        </Field>

        {!isDeleted ? (
          <>
            <button
              onClick={save} disabled={busy}
              style={{ background: T.red, color: '#FFFFFF', border: 0, padding: '14px', fontSize: 12, fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.15em', cursor: 'pointer', marginTop: 8, opacity: busy ? 0.5 : 1 }}
              data-testid="edit-save"
            >Save Changes</button>
            {draft.client_name && onOpenClient && (
              <button
                onClick={() => onOpenClient(booking.client_phone || booking.client_email || draft.client_name)}
                style={{ background: 'transparent', border: `1px solid ${T.border}`, color: T.light, padding: '10px', fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.15em', cursor: 'pointer' }}
                data-testid="edit-open-client"
              >View Client Profile →</button>
            )}
            <button
              onClick={remove} disabled={busy}
              style={{ background: 'transparent', border: `1px solid ${T.red}`, color: T.red, padding: '12px', fontSize: 11, fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.15em', cursor: 'pointer', marginTop: 16, opacity: busy ? 0.5 : 1 }}
              data-testid="edit-delete"
            >Delete Booking</button>
          </>
        ) : (
          <button
            onClick={restore} disabled={busy}
            style={{ background: T.green, color: T.black, border: 0, padding: '14px', fontSize: 12, fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.15em', cursor: 'pointer', marginTop: 8, opacity: busy ? 0.5 : 1 }}
            data-testid="edit-restore"
          >Restore Booking</button>
        )}
      </div>
    </div>
  );
};
